import { ensureMatterArtifactPrivacy } from './ensure-matter-artifact-privacy'
import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { saveAs } from 'file-saver'
import { nanoid } from 'nanoid'

import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { kMatterArtifactNameFallback, kMatterArtifactVersionsBasePath } from '@/matter-artifacts/constants'

async function fetchMatterArtifactDocx(matterArtifactVersionId: string): Promise<{ blob: Blob; filename: string }> {
  const token = await getAuth().currentUser?.getIdToken(true)
  if (!token) throw new Error(kErrorCodeAuthError)
  await ensureMatterArtifactPrivacy(matterArtifactVersionId, token)

  const transactionId = nanoid()
  const url = `${kPaxtonAppApiRESTBaseUrl()}${kMatterArtifactVersionsBasePath}/${encodeURIComponent(matterArtifactVersionId)}/docx`

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Transaction-Id': transactionId,
      },
    })
    if (!response.ok) throw new Error(`Failed to download Matter artifact DOCX. Status: ${response.status}`)

    const contentDisposition = response.headers.get('Content-Disposition')
    const filenameMatch = contentDisposition?.match(/filename="(.+)"/)
    const filename = filenameMatch?.[1] || `${kMatterArtifactNameFallback}.docx`
    return { blob: await response.blob(), filename }
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        matterArtifactVersionId,
        onLine: navigator.onLine,
        cookieEnabled: navigator.cookieEnabled,
      },
    })
    throw error
  }
}

export async function downloadMatterArtifactAsDocx(matterArtifactVersionId: string): Promise<string> {
  const { blob, filename } = await fetchMatterArtifactDocx(matterArtifactVersionId)
  saveAs(blob, filename)
  return filename
}

/** Fetch the plain (untemplated) docx render of a version as a blob, for saving to matter files. */
export async function fetchMatterArtifactDocxBlob(matterArtifactVersionId: string): Promise<{ blob: Blob }> {
  const { blob } = await fetchMatterArtifactDocx(matterArtifactVersionId)
  return { blob }
}
